import type { TFunction } from "i18next";
import type { FacilityNode } from "@arc/contracts";

/**
 * Copy for the archive / unarchive / duplicate confirmations.
 *
 * **The count must be the SERVER's, not the client tree's (#108).** These operations cascade over the whole subtree
 * (`path <@`, unfiltered), but the tree the client holds is permission-FILTERED: a scoped `access_manage` caller sees
 * only the facilities they may view. Counting that tree told such a caller "and its 2 subfacilities" while the server
 * archived seven — a confirmation that understated its own blast radius, which is worse than showing none.
 *
 * `descendants` therefore comes from `GET /facilities/:id/stats`, which counts the real subtree and requires exactly
 * the same authz these operations do (`requireManageSubtree` — `facilities.service.ts:401/535/667`), so reading it
 * adds no 403 the action itself wouldn't already raise. `null` means "not known yet / could not be fetched": the copy
 * then says the subtree travels along WITHOUT naming a number, because a wrong number is the bug being fixed.
 */
const K = "facilityManagement.confirm";

function withSubs(descendants: number | null, t: TFunction): string {
  // `t` is threaded rather than hooked: these are plain functions, called from event handlers as well as render.
  if (descendants === null) return t(`${K}.withSubsUnknown`);
  if (descendants === 0) return "";
  return t(`${K}.withSubs`, { count: descendants });
}

/** Copy for the archive/unarchive confirm — names the subtree size (both cascade the whole subtree). */
export function lifecycleConfirmMessage(
  kind: "archive" | "unarchive",
  node: FacilityNode,
  descendants: number | null,
  t: TFunction,
): string {
  const subs = withSubs(descendants, t);
  return t(kind === "archive" ? `${K}.archivePrompt` : `${K}.restorePrompt`, { name: node.name, subs });
}

/** Copy for the duplicate confirm — names the subtree size (the whole subtree is deep-copied) + the "… Copy" result. */
export function duplicateConfirmMessage(node: FacilityNode, descendants: number | null, t: TFunction): string {
  return t(`${K}.duplicatePrompt`, { name: node.name, subs: withSubs(descendants, t) });
}
